import { useCallback, useEffect, useState } from "react";
import { makeStyles } from "@mui/styles";
import SimulationAreaDropdown from "./SimulationAreaDropdown";
import PalmMap from "./PalmMap";

const useStyles = makeStyles({
    simulationAreaComponent: {
        display: "flex",
        height: "750px",
        width: "110%",
        marginTop: "20px",
    },
});

export default function PalmPage({ type, simulationAreas, simulationSpans, coefficients, failedSpans, terrainData }) {
    const classes = useStyles();
    const [groupName, setGroupName] = useState(null);
    const [selectedAreaId, setSelectedAreaId] = useState(null);
    const [mapCenter, setMapCenter] = useState({ latitude: 0, longitude: 0 });
    const [zoomLevel, setZoomLevel] = useState(9);
    const [spanCircleCenter, setSpanCircleCenter] = useState(null);
    const [currentCoefficients, setCurrentCoefficients] = useState(null);

    const weatherSensors = (type, coefficients) => {
        if (type === "weatherSensor" && coefficients) {
            return coefficients.weather_sensor_coefficients.map((ws) => ({
                serialNo: ws.weather_sensor_sn,
                latitude: ws.latitude,
                longitude: ws.longitude,
            }));
        }
        return null;
    };

    const getCenterOfArea = (area) => {
        const { max_latitude, min_latitude, max_longitude, min_longitude } = area.bbox;
        return { latitude: (+max_latitude + +min_latitude) / 2, longitude: (+max_longitude + +min_longitude) / 2 };
    };

    const getSpanCoordinates = (spanName, spansData) => {
        // Search for the span with the given name
        const span = spansData.find((s) => s.name === spanName);

        if (!span) {
            // Return some default or error value if the span is not found
            return { start: null, end: null };
        }

        // Extract the start and end coordinates
        const start = {
            latitude: span.start_structure.latitude,
            longitude: span.start_structure.longitude,
        };

        const end = {
            latitude: span.end_structure.latitude,
            longitude: span.end_structure.longitude,
        };

        return { latitude: (+start.latitude + +end.latitude) / 2, longitude: (+start.longitude + +end.longitude) / 2 };
    };

    const handleAreaSelect = useCallback((area) => {
        setMapCenter(getCenterOfArea(area));
        setZoomLevel(12);
        setSpanCircleCenter(null);
        setGroupName(area.group_name);
        setSelectedAreaId(area.id);
    }, []);

    const handleSpanSelect = useCallback(
        (spanName) => {
            const spanCoords = getSpanCoordinates(spanName, simulationSpans.spans);
            setMapCenter((_) => spanCoords);
            setZoomLevel(14);
            setSpanCircleCenter((_) => spanCoords);
            if (coefficients !== undefined) {
                const currentCoefficients = coefficients.span_coefficients.find((s) => s.span_id === spanName);
                setCurrentCoefficients((_) => currentCoefficients);
            }
        },
        [groupName]
    );

    const handleWeatherSensorSelect = useCallback(
        (weatherSensorSn) => {
            if (coefficients !== undefined) {
                const currentCoefficients = coefficients.weather_sensor_coefficients.find(
                    (ws) => ws.weather_sensor_sn === weatherSensorSn
                );
                setCurrentCoefficients((_) => currentCoefficients);
                let weatherSensorCoords = { latitude: currentCoefficients.latitude, longitude: currentCoefficients.longitude };
                setMapCenter((_) => weatherSensorCoords);
                setZoomLevel(14);
                setSpanCircleCenter((_) => weatherSensorCoords);
            }
        },
        [groupName]
    );

    useEffect(() => {
        const getCenterOfAllAreas = (areas) => {
            // We have to find the average of all given simulation areas
            let totalLat = 0;
            let totalLng = 0;
            areas.forEach((area) => {
                const { max_latitude, min_latitude, max_longitude, min_longitude } = area.bbox;
                totalLat += (+max_latitude + +min_latitude) / 2;
                totalLng += (+max_longitude + +min_longitude) / 2;
            });
            console.log("totalLat, totalLng, areas.length", totalLat, totalLng, areas.length);
            return { latitude: totalLat / areas.length, longitude: totalLng / areas.length };
        };

        // On mount, make sure we can see all of the simulation areas
        const center = getCenterOfAllAreas(simulationAreas.simulation_areas);
        setMapCenter((_) => center);
        setZoomLevel(9);
    }, [simulationAreas, coefficients]);

    return (
        <div className={classes.simulationAreaComponent}>
            <SimulationAreaDropdown
                type={type}
                simulationAreas={simulationAreas}
                simulationSpans={simulationSpans}
                weatherSensors={weatherSensors(type, coefficients)}
                coefficients={coefficients ? currentCoefficients : undefined}
                onSpanSelect={handleSpanSelect}
                onWeatherSensorSelect={handleWeatherSensorSelect}
                onAreaSelect={handleAreaSelect}
                failedSpans={failedSpans}
            />
            <PalmMap
                simulationAreas={simulationAreas}
                simulationSpans={simulationSpans}
                coefficients={coefficients ? currentCoefficients : undefined}
                allSpanCoefficients={coefficients?.span_coefficients}
                center={mapCenter}
                zoom={zoomLevel}
                spanCircleCenter={spanCircleCenter}
                terrainData={terrainData}
                selectedAreaKey={selectedAreaId}
            />
        </div>
    );
}
